import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, getParticipantId, getNickname, setNickname, BACKEND_URL } from "@/lib/api";
import { SLIDE_META } from "@/lib/slides";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, WifiOff, Send, ThumbsUp, ChevronUp, ChevronDown, Radio, Lock,
  Clock, Trophy, XCircle,
} from "lucide-react";

function vibrate(ms = 18) {
  if (navigator.vibrate) navigator.vibrate(ms);
}

function playBeep(freq = 760, dur = 0.07) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = playBeep._ctx || (playBeep._ctx = new Ctx());
    if (ctx.state === "suspended") ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.start();
    o.stop(ctx.currentTime + dur);
  } catch {}
}

export default function Play() {
  const { code } = useParams();
  const navigate = useNavigate();
  const pid = getParticipantId();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [submittedSlides, setSubmittedSlides] = useState({});
  const [nickname, setNickState] = useState(getNickname());
  const timer = useRef(null);
  const activeIdRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await api.get(`/session/${code}/state`);
      setState(data);
      setError(null);
      const aid = data.active_slide?.id || null;
      if (aid !== activeIdRef.current) activeIdRef.current = aid;
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Session ended");
    }
  }, [code]);

  useEffect(() => {
    fetchState();
    timer.current = setInterval(fetchState, 2000);
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      clearInterval(timer.current);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [fetchState]);

  const markSubmitted = (slideId, val = true) => setSubmittedSlides((s) => ({ ...s, [slideId]: val }));

  const submitQuiz = async (slide, optionIndex) => {
    const nick = getNickname() || "Player";
    const { data } = await api.post(`/session/${code}/respond`, {
      participant_id: pid, slide_id: slide.id, value: optionIndex, nickname: nick,
    });
    vibrate();
    markSubmitted(slide.id);
    fetchState();
    return data;
  };

  const submit = async (slide, value) => {
    try {
      await api.post(`/session/${code}/respond`, { participant_id: pid, slide_id: slide.id, value, nickname: getNickname() || "Guest" });
      vibrate();
      markSubmitted(slide.id);
      toast.success("Response received!");
      fetchState();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  if (error && !state) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white gap-4 p-6 text-center">
        <Radio className="w-10 h-10 text-slate-500" />
        <p className="text-slate-300">{error}</p>
        <button onClick={() => navigate("/")} className="text-indigo-400 font-semibold">Enter another code</button>
      </div>
    );
  }
  if (!state) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>;
  }

  if (!nickname) {
    return <NicknameGate title={state.title} logoUrl={state.logo_url} code={code}
                         onSet={(n) => { setNickname(n); setNickState(n); }} />;
  }

  const slide = state.active_slide;
  const locked = state.session.voting_locked;
  const done = slide && submittedSlides[slide.id];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {offline && (
        <div className="bg-amber-500 text-white text-sm font-medium px-4 py-2 flex items-center gap-2 justify-center" data-testid="offline-banner">
          <WifiOff className="w-4 h-4" /> Network lost. Your vote will submit when reconnected.
        </div>
      )}

      <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
            <Radio className="w-3.5 h-3.5" />
          </span>
          <span className="truncate max-w-[40vw]">{state.title || "Live session"}</span>
        </div>
        <div className="flex items-center gap-2">
          {state.logo_url && (
            <img src={`${BACKEND_URL}${state.logo_url}`} alt="logo" className="h-10 w-auto max-w-[150px] object-contain" />
          )}
          <span className="font-mono-code text-xs text-slate-400">#{code}</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-6">
        {!slide ? (
          <div className="text-center py-24 text-slate-400">You're in! Waiting for the presenter to begin…</div>
        ) : (
          <div key={slide.id} className="animate-fade-up">
            <div className="text-xs font-semibold uppercase tracking-widest text-indigo-600 mb-2">{SLIDE_META[slide.type]?.label || "Live"}</div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 mb-6">{slide.question}</h1>

            {locked && slide.type !== "qa" ? (
              <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 text-center text-amber-700 flex flex-col items-center gap-2">
                <Lock className="w-6 h-6" /> Voting is locked by the presenter.
              </div>
            ) : (
              <SlideInput slide={slide} done={done} onSubmit={submit}
                          onReanswer={() => markSubmitted(slide.id, false)}
                          code={code} pid={pid} results={state.results} refresh={fetchState}
                          session={state.session} leaderboard={state.leaderboard} submitQuiz={submitQuiz} />
            )}
          </div>
        )}
      </main>

      <footer className="text-center py-4 text-xs text-slate-400">Powered by Pulse</footer>
    </div>
  );
}

function Confirmed({ onReanswer, allowChange = true }) {
  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center flex flex-col items-center gap-3 animate-pop-in">
      <CheckCircle2 className="w-12 h-12 text-emerald-500" />
      <p className="font-semibold text-emerald-800">Response received!</p>
      {allowChange && (
        <button onClick={onReanswer} className="text-sm text-emerald-700 font-medium hover:underline" data-testid="change-answer-button">
          Change my answer
        </button>
      )}
    </div>
  );
}

function SlideInput({ slide, done, onSubmit, onReanswer, code, pid, results, refresh, session, leaderboard, submitQuiz }) {
  const type = slide.type;

  if (type === "quiz") {
    return <QuizInput slide={slide} session={session} submitQuiz={submitQuiz} leaderboard={leaderboard} />;
  }

  if (type === "multiple_choice") {
    if (done) return <Confirmed onReanswer={onReanswer} />;
    return (
      <div className="space-y-3">
        {slide.options.map((o, i) => (
          <button key={i} data-testid={`audience-vote-option-${i}`} onClick={() => onSubmit(slide, i)}
                  className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50/40 active:scale-[0.98] font-medium text-slate-800 transition-all">
            {o}
          </button>
        ))}
      </div>
    );
  }

  if (type === "scales") {
    return <ScaleInput slide={slide} done={done} onSubmit={onSubmit} onReanswer={onReanswer} />;
  }

  if (type === "ranking") {
    if (done) return <Confirmed onReanswer={onReanswer} />;
    return <RankInput slide={slide} onSubmit={onSubmit} />;
  }

  if (type === "word_cloud") {
    return <WordInput slide={slide} onSubmit={onSubmit} />;
  }

  if (type === "open_ended") {
    return <OpenInput slide={slide} onSubmit={onSubmit} />;
  }

  if (type === "qa") {
    return <QAInput slide={slide} code={code} pid={pid} results={results} refresh={refresh} />;
  }

  return null;
}

function ScaleInput({ slide, done, onSubmit, onReanswer }) {
  const [val, setVal] = useState(Math.round((slide.scale_min + slide.scale_max) / 2));
  if (done) return <Confirmed onReanswer={onReanswer} />;
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-6xl font-display font-extrabold text-indigo-600 tabular-nums">{val}</div>
      </div>
      <input
        data-testid="audience-scale-slider"
        type="range" min={slide.scale_min} max={slide.scale_max} value={val}
        onChange={(e) => setVal(parseInt(e.target.value, 10))}
        className="w-full accent-indigo-600 h-2"
      />
      <div className="flex justify-between text-xs text-slate-500">
        <span>{slide.scale_min_label} ({slide.scale_min})</span>
        <span>{slide.scale_max_label} ({slide.scale_max})</span>
      </div>
      <SubmitBtn onClick={() => onSubmit(slide, val)} />
    </div>
  );
}

function RankInput({ slide, onSubmit }) {
  const [order, setOrder] = useState(slide.options.map((_, i) => i));
  const move = (pos, dir) => {
    const next = [...order];
    const t = pos + dir;
    if (t < 0 || t >= next.length) return;
    [next[pos], next[t]] = [next[t], next[pos]];
    setOrder(next);
  };
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {order.map((optIdx, pos) => (
          <div key={optIdx} className="flex items-center gap-3 p-3 rounded-xl border-2 border-slate-200 bg-white">
            <span className="w-7 h-7 shrink-0 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center text-sm">{pos + 1}</span>
            <span className="flex-1 font-medium text-slate-800">{slide.options[optIdx]}</span>
            <div className="flex flex-col">
              <button onClick={() => move(pos, -1)} data-testid={`rank-up-${pos}`} className="text-slate-400 hover:text-indigo-600"><ChevronUp className="w-5 h-5" /></button>
              <button onClick={() => move(pos, 1)} data-testid={`rank-down-${pos}`} className="text-slate-400 hover:text-indigo-600"><ChevronDown className="w-5 h-5" /></button>
            </div>
          </div>
        ))}
      </div>
      <SubmitBtn onClick={() => onSubmit(slide, order)} />
    </div>
  );
}

function WordInput({ slide, onSubmit }) {
  const [word, setWord] = useState("");
  const [count, setCount] = useState(0);
  const send = () => {
    const w = word.trim();
    if (!w) return;
    onSubmit(slide, w);
    setWord("");
    setCount((c) => c + 1);
  };
  return (
    <div className="space-y-4">
      <input
        data-testid="audience-word-input"
        value={word}
        maxLength={40}
        onChange={(e) => setWord(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && send()}
        placeholder="Type a word…"
        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-lg text-slate-900"
      />
      <SubmitBtn onClick={send} label="Submit word" testid="audience-word-submit" />
      {count > 0 && <p className="text-center text-sm text-emerald-600 font-medium">Sent {count} word{count > 1 ? "s" : ""}! Add more if you like.</p>}
    </div>
  );
}

function OpenInput({ slide, onSubmit }) {
  const [text, setText] = useState("");
  const [count, setCount] = useState(0);
  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSubmit(slide, t);
    setText("");
    setCount((c) => c + 1);
  };
  return (
    <div className="space-y-4">
      <textarea
        data-testid="audience-open-input"
        value={text}
        maxLength={280}
        rows={4}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share your thoughts…"
        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-slate-900 resize-none"
      />
      <div className="text-right text-xs text-slate-400">{text.length}/280</div>
      <SubmitBtn onClick={send} label="Send" testid="audience-open-submit" />
      {count > 0 && <p className="text-center text-sm text-emerald-600 font-medium">Sent! You can submit another.</p>}
    </div>
  );
}

function QAInput({ slide, code, pid, results, refresh }) {
  const [text, setText] = useState("");
  const questions = results?.questions || [];

  const ask = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      await api.post(`/session/${code}/question`, { participant_id: pid, text: t, author: getNickname() || "Guest" });
      vibrate();
      setText("");
      toast.success("Question submitted!");
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const upvote = async (qid) => {
    try {
      await api.post(`/session/${code}/question/${qid}/upvote`, { participant_id: pid, slide_id: slide.id, value: 1 });
      vibrate(12);
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <textarea
          data-testid="qa-question-input"
          value={text}
          maxLength={200}
          rows={3}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask a question…"
          className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-slate-900 resize-none"
        />
        <SubmitBtn onClick={ask} label="Submit question" testid="qa-submit-button" />
      </div>
      <div className="space-y-2">
        {questions.map((q) => (
          <div key={q.id} className={`rounded-xl border p-3 flex items-start gap-3 ${q.answered ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
            <button data-testid={`qa-upvote-button-${q.id}`} onClick={() => upvote(q.id)}
                    className="flex flex-col items-center text-indigo-600 active:scale-125 transition-transform">
              <ThumbsUp className="w-5 h-5" />
              <span className="text-xs font-bold">{q.upvotes}</span>
            </button>
            <p className={`flex-1 text-slate-800 text-sm ${q.answered ? "line-through text-slate-400" : ""}`}>{q.text}</p>
          </div>
        ))}
        {!questions.length && <p className="text-center text-slate-400 text-sm py-6">Be the first to ask!</p>}
      </div>
    </div>
  );
}

function QuizInput({ slide, session, submitQuiz, leaderboard }) {
  const [nick, setNick] = useState(getNickname());
  const [nickDraft, setNickDraft] = useState("");
  const [feedback, setFeedback] = useState(null); // {correct, points}
  const [chosen, setChosen] = useState(null);
  const [now, setNow] = useState(Date.now());
  const lastTick = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  const startedAt = session?.question_started_at ? new Date(session.question_started_at).getTime() : now;
  const deadline = startedAt + (slide.time_limit || 20) * 1000;
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  const timeUp = remaining <= 0;
  const pct = Math.max(0, Math.min(100, ((deadline - now) / ((slide.time_limit || 20) * 1000)) * 100));

  useEffect(() => {
    if (feedback || !nick) return;
    if (remaining > 0 && remaining <= 5 && lastTick.current !== remaining) {
      lastTick.current = remaining;
      playBeep(760, 0.07);
    }
    if (remaining === 0 && lastTick.current !== "end") {
      lastTick.current = "end";
      playBeep(380, 0.35);
      vibrate(60);
    }
  }, [remaining, feedback, nick]);

  if (!nick) {
    return (
      <div className="space-y-4">
        <p className="text-slate-600">Enter a nickname to join the quiz leaderboard:</p>
        <input
          data-testid="quiz-nickname-input"
          value={nickDraft}
          maxLength={20}
          onChange={(e) => setNickDraft(e.target.value)}
          placeholder="Your nickname"
          className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-lg text-slate-900"
        />
        <SubmitBtn testid="quiz-nickname-submit" label="Join quiz" onClick={() => {
          const n = nickDraft.trim();
          if (!n) { toast.error("Pick a nickname"); return; }
          setNickname(n);
          setNick(n);
        }} />
      </div>
    );
  }

  if (feedback) {
    return (
      <div className={`rounded-2xl border-2 p-8 text-center flex flex-col items-center gap-3 animate-pop-in ${feedback.correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
        {feedback.correct
          ? <CheckCircle2 className="w-14 h-14 text-emerald-500" />
          : <XCircle className="w-14 h-14 text-rose-500" />}
        <p className={`font-display font-extrabold text-2xl ${feedback.correct ? "text-emerald-700" : "text-rose-700"}`}>
          {feedback.correct ? "Correct!" : "Not quite"}
        </p>
        <p className="font-mono-code text-slate-600">+{feedback.points} points</p>
        <p className="text-sm text-slate-400 flex items-center gap-1"><Trophy className="w-4 h-4" /> Waiting for the next question…</p>
      </div>
    );
  }

  if (timeUp) {
    return (
      <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-8 text-center flex flex-col items-center gap-2">
        <Clock className="w-10 h-10 text-slate-400" />
        <p className="font-semibold text-slate-600">Time's up! Waiting for the next question…</p>
      </div>
    );
  }

  const answer = async (i) => {
    setChosen(i);
    try {
      const data = await submitQuiz(slide, i);
      setFeedback({ correct: data.correct, points: data.points });
    } catch (err) {
      setChosen(null);
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-500 flex items-center gap-1.5"><Trophy className="w-4 h-4 text-amber-500" /> {nick}</span>
        <span className={`font-mono-code font-bold text-lg tabular-nums ${remaining <= 5 ? "text-rose-600" : "text-indigo-600"}`}>
          <Clock className="w-4 h-4 inline mb-0.5" /> {remaining}s
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-indigo-500 transition-[width] duration-200" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-1 gap-3 pt-2">
        {slide.options.map((o, i) => (
          <button key={i} data-testid={`quiz-option-${i}`} disabled={chosen !== null} onClick={() => answer(i)}
                  className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50/40 active:scale-[0.98] font-medium text-slate-800 transition-all disabled:opacity-50">
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function NicknameGate({ title, logoUrl, code, onSet }) {
  const [name, setName] = useState("");
  const submit = () => {
    const n = name.trim();
    if (!n) { toast.error("Please enter your name"); return; }
    onSet(n);
    vibrate();
  };
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          {logoUrl ? (
            <img src={`${BACKEND_URL}${logoUrl}`} alt="logo" className="h-9 w-auto max-w-[150px] object-contain" />
          ) : (
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white"><Radio className="w-3.5 h-3.5" /></span>
          )}
          <span className="truncate max-w-[40vw]">{title || "Live session"}</span>
        </div>
        <span className="font-mono-code text-xs text-slate-400">#{code}</span>
      </header>
      <main className="flex-1 w-full max-w-md mx-auto px-5 py-12">
        <div className="animate-fade-up">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mb-2">What's your name?</h1>
          <p className="text-slate-500 mb-6">Enter your name to join and appear on the leaderboard.</p>
          <input
            data-testid="join-nickname-input"
            value={name}
            maxLength={24}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Your name"
            className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-lg text-slate-900 mb-4"
          />
          <SubmitBtn testid="join-nickname-submit" label="Join session" onClick={submit} />
        </div>
      </main>
    </div>
  );
}

function SubmitBtn({ onClick, label = "Submit", testid = "audience-vote-submit-button" }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold rounded-xl px-5 py-3.5 shadow-md shadow-indigo-200 transition-all">
      <Send className="w-4 h-4" /> {label}
    </button>
  );
}
